<script lang="ts" setup>
import { MapComponent, MapTypes } from '@neshan-maps-platform/mapbox-gl-vue';
import '@neshan-maps-platform/mapbox-gl-vue/dist/style.css';
import { Marker, Popup } from '@neshan-maps-platform/mapbox-gl';
import type SDKMap from '@neshan-maps-platform/mapbox-gl/dist/src/core/Map';
import { faMoon, faBrightness } from '@fortawesome/pro-solid-svg-icons';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { type HotelSearchView } from '@clients/HotelApi';
import type HotelPackage from '@itinerary/hotel/HotelPackage';
import { toAbsoluteSrc } from '@store/content';

type CameraState = {
    center: [number, number];
    zoom: number;
    bearing: number;
    pitch: number;
};

const { simpleMarker, points, center } = defineProps<{
    points: {
        hotel: HotelPackage;
        lat: number;
        lng: number;
    }[];
    center: [number, number];
    // Optional standalone marker
    simpleMarker?: {
        lat: number;
        lng: number;
    } | null;
}>();

const emit = defineEmits<{
    hotelClick: [HotelPackage];
}>();

const darkModeIcons = {
    [MapTypes.neshanVector]: faMoon,
    [MapTypes.neshanVectorNight]: faBrightness,
};

const { isMobile } = useSize();

const mapType = ref<MapTypes.neshanVector | MapTypes.neshanVectorNight>(
    MapTypes.neshanVector
);

const simpleMapMarker = shallowRef<InstanceType<typeof Marker> | null>(null);
const mapIsLoaded = ref(false);

function showSimpleMarker(map: SDKMap) {
    // Remove the previous marker, if one exists
    simpleMapMarker.value?.remove();
    simpleMapMarker.value = null;

    if (!simpleMarker) {
        return;
    }

    simpleMapMarker.value = new Marker()
        .setLngLat([simpleMarker.lng, simpleMarker.lat])
        .addTo(map as any);
}

const lastCamera = ref<CameraState | null>(null);
const mapSdk = ref<SDKMap>();

function captureCamera() {
    const map = mapSdk.value as any;
    if (!map) {
        return;
    }

    const c = map.getCenter();
    lastCamera.value = {
        center: [c.lng, c.lat],
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
    };
}

const options = computed(() => ({
    mapKey: 'web.cb673dab545e43ac978d725fac53f24b',
    mapType: mapType.value,
    zoom: 11,
    center,
    isTouchPlatform: isMobile.value,
    poi: false,
    poiControllerOptions: {
        show: false,
    },
    trafficControllerOptions: {
        show: false,
    },
    mapTypeControllerOptions: {
        show: false,
    },
}));

function roundByIndex(n: number) {
    if (n >= 9500000) {
        const place = 10 ** (7 - 1);
        const price = Math.round(n / place) * place;
        const formattedPrice = price / 10000000;
        return `${Number(formattedPrice.toFixed(1)).toString()} میلیون`;
    } else {
        return `${Math.trunc(n / 10000)} هزار`;
    }
}

let htmlMarkers: any[] = [];

const MARKERS_MIN_ZOOM = 11; // choose what feels right
const htmlMarkersById = new Map<string, any>();
const blockedEvents = [
    'click',
    'dblclick',
    'pointerdown',
    'pointerup',
    'contextmenu',
];

function showHtmlMarkers(map: any) {
    // clearHtmlMarkers();
    // keep your behavior (you call this elsewhere)
    // BUT: don't rely on clearHtmlMarkers() to remove everything every time,
    // because we want conditional cleanup (and performance).
    // If clearHtmlMarkers() currently removes markers from the map, comment it out.
    // If it only resets your htmlMarkers array, it's OK to keep.
    // clearHtmlMarkers();

    // Zoom gate: below threshold => remove ALL custom markers, clusters still remain (source unchanged)
    if (map.getZoom() < MARKERS_MIN_ZOOM) {
        for (const marker of htmlMarkersById.values()) {
            marker.remove();
        }
        htmlMarkersById.clear();
        htmlMarkers.length = 0; // keep your array in sync
        return;
    }

    // 1) Get visible unclustered features (on-screen)
    const features = map.queryRenderedFeatures({
        layers: ['unclustered-query-layer'],
    });

    // Track which markers should exist after this run
    const keepIds = new Set<string>();

    features.forEach((f: any) => {
        const id = f?.properties?.title;
        if (!id) {
            return;
        }

        const hotel = points.find(p => p.hotel.uniqueId === id)?.hotel;

        // OPTIONAL: your condition goes here (example: only if payable exists)
        // If condition fails: don't show marker, BUT point still stays in clusters because source is unchanged.
        // if (!hotel?.payable) return;

        keepIds.add(id);

        // If marker already exists, just update its position and keep it
        const existing = htmlMarkersById.get(id);
        if (existing) {
            existing.setLngLat(f.geometry.coordinates);
            return;
        }

        // ---- Your original marker creation code (unchanged) ----
        const el = document.createElement('div');
        el.className = 'my-marker';
        el.innerHTML = `
        <div class="soke-container">
            <div class="popup-content-data">
     ${hotel?.payable ? roundByIndex(hotel.payable) : '-'}
            </div>
            <img src="/img/map-sok.svg" class="map-soke" alt="sok">
            <div class="circle" />
        </div>
        `;

        if (isMobile.value) {
            blockedEvents.forEach(evt => {
                el.addEventListener(
                    evt,
                    e => {
                        e.stopPropagation();
                    },
                    { passive: true }
                );
            });
        }

        if (isMobile.value) {
            el.addEventListener('click', e => {
                e.stopPropagation();
                if (hotel) {
                    emit('hotelClick', hotel);
                }
            });
        }

        const popup = new Popup({
            offset: [0, 0],
            closeButton: false,
            className: 'mr-map-popup',
        }).setHTML(`
                                    <div class="popup-opened-content-data">
                                        <img
                                        class="popup-image"
                                        src="${
                                            hotel?.photo.large ??
                                            toAbsoluteSrc(
                                                '/uploads/hotel_placeholder_v4_af8e033460.png'
                                            )
                                        }"
                                        alt="${hotel?.hotelName}"
                                        />
                                        <p>
${(hotel?.hotel as HotelSearchView)?.name ?? hotel?.hotelName}
                                            <br />

                                           <span class="money">
${formatMoney(hotel?.payable)}
                                           </span>
                                        </p>
                                        <img src="/img/map-sok.svg" class="map-soke" alt="sok">
                                    </div>
        </div>
`);

        const marker = new Marker(el)
            .setLngLat(f.geometry.coordinates)
            .setPopup(popup)
            .addTo(map);

        el
            .getElementsByClassName('popup-content-data')?.[0]
            ?.addEventListener('mouseenter', () => {
                if (isMobile.value) {
                    return;
                }
                document
                    .querySelectorAll('.mapboxgl-popup')
                    .forEach(p => p.remove());
                marker.togglePopup();
            });

        const popupClicked = () => {
            if (hotel) {
                emit('hotelClick', hotel);
            }
        };

        popup.on('open', (e: any) => {
            e?.target?._container?.addEventListener('mouseleave', () => {
                e?.target?._container?.removeEventListener(
                    'click',
                    popupClicked
                );
                marker.togglePopup();
            });
            e?.target?._container?.addEventListener('click', popupClicked);
        });

        // Store so we can keep/remove on next updates
        htmlMarkersById.set(id, marker);
        htmlMarkers.push(marker);
    });

    // 2) Remove markers that are no longer visible/unclustered or no longer match your condition
    for (const [id, marker] of htmlMarkersById.entries()) {
        if (!keepIds.has(id)) {
            marker.remove();
            htmlMarkersById.delete(id);
        }
    }

    // Keep your htmlMarkers array consistent (optional but nice)
    // (removes references to deleted markers)
    htmlMarkers = htmlMarkers.filter(
        _m =>
            // marker has no official "id", so we keep only those still in the map
            // easiest: rebuild from the Map
            true
    );
    // Better: rebuild the array from the Map:
    htmlMarkers.length = 0;
    for (const m of htmlMarkersById.values()) {
        htmlMarkers.push(m);
    }
}

function zoomIn() {
    (mapSdk.value as any)?.zoomIn();
}
function zoomOut() {
    (mapSdk.value as any)?.zoomOut();
}

function toggleMapType() {
    captureCamera();
    mapType.value =
        mapType.value === MapTypes.neshanVector
            ? MapTypes.neshanVectorNight
            : MapTypes.neshanVector;
}

watch(
    () => [simpleMarker?.lat, simpleMarker?.lng] as const,
    () => {
        if (!mapSdk.value || !mapIsLoaded.value) {
            return;
        }

        showSimpleMarker(mapSdk.value);
    }
);

const mapSetter = (map: SDKMap) => {
    // Add route
    mapSdk.value = map;
    (map as any).on('load', () => {
        mapIsLoaded.value = true;

        // Independent standalone marker
        showSimpleMarker(map);
        map.addSource('points1', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: points.map(({ lat, lng, hotel }) => ({
                    type: 'Feature',
                    properties: {
                        title: hotel.uniqueId,
                        description: hotel.description,
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat],
                    },
                })),
            },
            cluster: true,
            clusterMaxZoom: 16,
            clusterRadius: 40,
        });

        map.addLayer({
            id: 'clustered2',
            type: 'circle',
            source: 'points1',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#3b82f6',
                'circle-radius': 25,
            },
        });

        map.addLayer({
            id: 'clustered',
            type: 'symbol',
            source: 'points1',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': [
                    'format',
                    ['get', 'point_count_abbreviated'],
                    ' اقامتگاه',
                ],
                'text-size': 10,
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
            },
            paint: {
                'text-color': '#ffffff',
            },
        });

        map.addLayer({
            id: 'unclustered-query-layer',
            type: 'circle',
            source: 'points1',
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-radius': 1,
                'circle-opacity': 0, // invisible
            },
        });

        // Update HTML markers whenever map view changes.
        const updateHtmlMarkers = () => showHtmlMarkers(map);
        (map as any).on('click', 'clustered2', (e: any) => {
            const features = (map as any).queryRenderedFeatures(e.point, {
                layers: ['clustered2'],
            });
            if (!features.length) {
                return;
            }

            const [clusterFeature] = features;
            const clusterId = clusterFeature.properties.cluster_id;
            const { coordinates } = clusterFeature.geometry;

            const source: any = (map as any).getSource('points1');
            if (!source?.getClusterExpansionZoom) {
                return;
            }

            source.getClusterExpansionZoom(
                clusterId,
                (err: any, zoom: number) => {
                    if (err) {
                        return;
                    }

                    (map as any).easeTo({
                        center: coordinates,
                        zoom,
                        duration: 500,
                    });

                    // Optional: if you want to force-refresh HTML markers ASAP after the animation
                    // (usually not needed if you already update on 'idle')
                    (map as any).once('idle', () => showHtmlMarkers(map));
                }
            );
        });

        // Cursor pointer on clusters
        (map as any).on('mouseenter', 'clustered2', () => {
            (map as any).getCanvas().style.cursor = 'pointer';
        });
        (map as any).on('mouseleave', 'clustered2', () => {
            (map as any).getCanvas().style.cursor = '';
        });

        (map as any).on('moveend', updateHtmlMarkers);
        (map as any).on('zoomend', updateHtmlMarkers);

        if (lastCamera.value) {
            (map as any)?.jumpTo({
                center: lastCamera.value.center,
                zoom: lastCamera.value.zoom,
                bearing: lastCamera.value.bearing,
                pitch: lastCamera.value.pitch,
            });
        }
        // initial draw
        // updateHtmlMarkers();
        setTimeout(() => updateHtmlMarkers(), 400);
    });
};

onBeforeUnmount(() => {
    simpleMapMarker.value?.remove();
    simpleMapMarker.value = null;
});
</script>

<template>
    <div class="neshan-container">
        <MapComponent :key="mapType" :options :map-setter />
        <div class="control-panel">
            <div class="zoom-panel">
                <mr-button :icon-button="faPlus" @click="zoomIn" />
                <mr-divider
                    class="divider"
                    color="var(--cool-gray-500)"
                    :thickness="1"
                />
                <mr-button :icon-button="faMinus" @click="zoomOut" />
            </div>
            <mr-button
                :icon-button="darkModeIcons[mapType]"
                @click="toggleMapType"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.neshan-container {
    width: 100%;
    height: 100%;
    position: relative;
    border-radius: 8px;
    .control-panel {
        position: absolute;
        left: 20px;
        bottom: 20px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        @include mobile-only {
            left: 12px;
            bottom: 80px;
        }
        .zoom-panel {
            display: flex;
            flex-direction: column;
            background-color: usePalette($p-blue, $p-400);
            border-radius: 12px;
            .divider {
                padding-inline: 12px;
            }
        }
    }
}

:deep() {
    .wm-container {
        display: none !important;
    }
}

:deep() {
    .map-soke {
        width: 15px;
        height: 9.5px;
        transform: none !important;
        -webkit-transform: none !important;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
    }

    .mr-map-popup .mapboxgl-popup-content {
        border-radius: 12px;
        background-color: usePalette($p-blue, $p-400);
        color: white !important;
        position: relative;
        padding: 8px !important;
        padding-bottom: 0 !important;
        width: 136px;
    }

    .mr-map-popup .mapboxgl-popup-tip {
        // border-top-color: usePalette($p-blue, $p-500);
        display: none;
    }

    .popup-opened-content-data {
        font: unset !important;
        font-feature-settings: 'ss02' !important;
        font-family: IRANSans-XV, serif !important;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center !important;
        text-align: center !important;
        width: 100%;
        // gap: 8px;

        p {
            color: white !important;
            @include tg-m();
            text-align: center;
        }
        .map-soke {
            position: absolute;
            margin-bottom: -6px;
            bottom: 0;
        }
        .popup-image {
            border-radius: 8px;
            width: 120px;
            height: 120px;
            object-fit: cover;
        }
        .money {
            font: unset !important;
            font-feature-settings: 'ss02' !important;
            font-family: IRANSans-XV, serif !important;
            text-align: center;
            color: white !important;
            @include tg-l($w7);
        }
    }
    .soke-container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .popup-content-data {
            font: unset !important;
            font-feature-settings: 'ss02' !important;
            font-family: IRANSans-XV, serif !important;
            color: white !important;
            @include tg-l($w7);
            background-color: usePalette($p-blue, $p-400);
            padding-inline: 8px;
            border: 8px;
            border-radius: 12px;
            direction: rtl;
        }

        .map-soke {
            width: 15px;
            height: 9.5px;
            margin-top: -3px;
        }
        .circle {
            width: 16px;
            height: 16px;
            background: usePalette($p-blue, $p-400);
            border: solid 2px usePalette($p-blue, $p-500);
            border-radius: 50%;
        }
    }
}
</style>
